import React, { useMemo, useState } from 'react';
import {
  ComposableMap, Geographies, Geography, Marker, ZoomableGroup,
} from 'react-simple-maps';
import { motion } from 'framer-motion';
import { Truck, MapPin, Sparkles } from 'lucide-react';
import { MAP_CITIES, findCityById, haversineKm } from '../../data/mapCities';
import { deriveContractDestination } from '../../utils/mapRoutes';
import { useTranslation } from '../../i18n';

const GEO_URL = '/geo/europe.topojson';

// Per-phase viewport tuning so the map auto-zooms as the player grows.
const PHASE_VIEWPORT = {
  1: { center: [2.5, 47.5], scale: 1400, focusKey: 'map_focus_p1' },
  2: { center: [2.5, 47.0], scale: 1300, focusKey: 'map_focus_p2' },
  3: { center: [3.5, 46.5], scale: 1150, focusKey: 'map_focus_p3' },
  4: { center: [6.0, 48.0], scale: 800,  focusKey: 'map_focus_p4' },
};

// Category → stroke colour (matches the rest of the UI palette).
// Keys are data lookup keys matching contract.category (FR by design).
const CATEGORY_COLOR = {
  'Messagerie / Colis': '#3B82F6',
  'BTP / Matériaux':    '#F59E0B', // i18n-keep
  'Frigorifique':       '#06B6D4',
  'Spécialisé':         '#EF4444', // i18n-keep
  'Export EU':          '#A855F7',
  'Vrac / Palettes':    '#14B8A6',
  'default':            '#94A3B8',
};

/**
 * Build a Bézier SVG path between two projected points. The control point is
 * placed perpendicular to the chord, at a curvature proportional to the
 * straight-line length. Looks natural without overshooting.
 */
const buildBezierPath = (a, b) => {
  const [ax, ay] = a;
  const [bx, by] = b;
  const mx = (ax + bx) / 2;
  const my = (ay + by) / 2;
  const dx = bx - ax;
  const dy = by - ay;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  // Perpendicular offset (scaled). Negative for an upward curve.
  const offset = len * 0.18;
  const px = mx + (-dy / len) * offset;
  const py = my + (dx / len) * offset;
  return `M ${ax} ${ay} Q ${px} ${py} ${bx} ${by}`;
};

const HQ_BONUS_RADIUS_KM = 200;

export function InteractiveMap({
  hqCityId,
  phase = 1,
  activeContracts = [],
  vehicles = [],
  drivers = [],
  selectable = false,
  onSelectCity = null,
  selectableFilter = null, // optional fn(city) => bool to gate which cities are clickable
  showRoutesPanel = true,
}) {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(null);

  const viewport = PHASE_VIEWPORT[phase] || PHASE_VIEWPORT[1];
  const focusLabel = t(viewport.focusKey);
  const visibleCities = useMemo(
    () => MAP_CITIES.filter(c => c.phase <= phase),
    [phase]
  );
  const hq = findCityById(hqCityId);
  const isCitySelectable = (city) => {
    if (!selectable) return false;
    if (typeof selectableFilter === 'function') return selectableFilter(city);
    return city.phase <= phase;
  };

  // Build active route list (HQ → destination derived per contract).
  const routes = useMemo(() => {
    if (selectable) return []; // no routes in selection mode
    if (!hq) return [];
    return activeContracts.map(c => {
      const dest = deriveContractDestination(c, hqCityId, phase);
      if (!dest) return null;
      const vehicle = vehicles.find(v => v.id === c.vehicleId);
      const driver = drivers.find(d => d.id === c.driverId);
      const distance = haversineKm(hq, dest);
      const isLocalBonus = distance <= HQ_BONUS_RADIUS_KM;
      return {
        contract: c, dest, vehicle, driver, distance, isLocalBonus,
        color: CATEGORY_COLOR[c.category] || CATEGORY_COLOR.default,
      };
    }).filter(Boolean);
  }, [activeContracts, hqCityId, phase, hq, vehicles, drivers, selectable]);

  return (
    <div className="w-full h-full relative bg-ink-900" data-testid="interactive-map">
      <style>{`
        @keyframes hst-map-pulse {
          0%   { opacity: 0.65; transform: scale(1); }
          70%  { opacity: 0; transform: scale(2.2); }
          100% { opacity: 0; transform: scale(2.2); }
        }
      `}</style>
      {/* Phase indicator */}
      <div className="absolute top-3 left-3 z-10 bg-ink-800/85 backdrop-blur border border-ink-600 rounded-md px-2.5 py-1.5 text-[10px] font-mono uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
        <Sparkles className="w-3 h-3 text-accent" />
        {selectable
          ? t('map_phase_select_city', { phase, focus: focusLabel })
          : t('map_phase_missions_in_progress', { phase, focus: focusLabel, count: routes.length })}
      </div>

      {/* Legend (categories present in active routes) */}
      {!selectable && routes.length > 0 && (
        <div className="absolute top-3 right-3 z-10 bg-ink-800/85 backdrop-blur border border-ink-600 rounded-md p-2 text-[9px] font-mono text-slate-300 space-y-1" data-testid="map-legend">
          {[...new Set(routes.map(r => r.contract.category))].map(cat => (
            <div key={cat} className="flex items-center gap-1.5">
              <span className="w-3 h-0.5" style={{ backgroundColor: CATEGORY_COLOR[cat] || CATEGORY_COLOR.default }} />
              <span>{cat}</span>
            </div>
          ))}
        </div>
      )}

      <ComposableMap
        projection="geoMercator"
        projectionConfig={{ center: viewport.center, scale: viewport.scale }}
        width={800}
        height={500}
        style={{ width: '100%', height: '100%', background: '#0B1220' }}
      >
        <ZoomableGroup zoom={1} maxZoom={3} minZoom={0.8}>
          <Geographies geography={GEO_URL}>
            {({ geographies, projection }) => (
              <>
                {/* Country shapes — France highlighted, others dimmed */}
                {geographies.map((geo) => {
                  const isFR = geo.properties.name === 'France';
                  const isHovered = hovered === geo.rsmKey;
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      onMouseEnter={() => setHovered(geo.rsmKey)}
                      onMouseLeave={() => setHovered(null)}
                      style={{
                        default: {
                          fill: isFR ? '#1E3A8A' : '#16213A',
                          stroke: isFR ? '#3B82F6' : '#1A233A',
                          strokeWidth: 0.5,
                          outline: 'none',
                        },
                        hover: {
                          fill: isFR ? '#1D4ED8' : (isHovered ? '#1F2D4D' : '#16213A'),
                          stroke: '#60A5FA',
                          strokeWidth: 0.7,
                          outline: 'none',
                        },
                        pressed: { fill: '#1E40AF', outline: 'none' },
                      }}
                    />
                  );
                })}

                {/* HQ radius (200 km bonus) — drawn as a circle in projected px */}
                {hq && (() => {
                  const [cx, cy] = projection([hq.lng, hq.lat]) || [0, 0];
                  // Approximate 200 km in projected pixels: project a point
                  // 200 km north and compute pixel delta.
                  const northPoint = projection([hq.lng, hq.lat + (HQ_BONUS_RADIUS_KM / 111)]);
                  const radiusPx = northPoint ? Math.abs(northPoint[1] - cy) : 30;
                  return (
                    <circle
                      cx={cx} cy={cy} r={radiusPx}
                      fill="rgba(16, 185, 129, 0.08)"
                      stroke="#10B981"
                      strokeWidth={0.6}
                      strokeDasharray="2,2"
                      data-testid="hq-radius"
                    />
                  );
                })()}

                {/* Bézier route paths + animated truck markers */}
                {routes.map((r, idx) => {
                  if (!hq) return null;
                  const aXY = projection([hq.lng, hq.lat]);
                  const bXY = projection([r.dest.lng, r.dest.lat]);
                  if (!aXY || !bXY) return null;
                  const path = buildBezierPath(aXY, bXY);
                  return (
                    <g key={r.contract.instanceId || idx} data-testid={`route-${r.contract.instanceId}`}>
                      {/* The stroke path */}
                      <motion.path
                        d={path}
                        fill="none"
                        stroke={r.color}
                        strokeWidth={1.2}
                        strokeLinecap="round"
                        strokeDasharray="3,3"
                        initial={{ pathLength: 0, opacity: 0 }}
                        animate={{ pathLength: 1, opacity: 0.85 }}
                        transition={{ duration: 1.2, delay: idx * 0.1 }}
                      />
                      {/* The animated truck dot following the path */}
                      <motion.circle
                        r={2.4}
                        fill={r.color}
                        stroke="#0B1220"
                        strokeWidth={0.8}
                        animate={{
                          offsetDistance: ['0%', '100%'],
                        }}
                        transition={{
                          duration: 6 + idx * 1.5,
                          repeat: Infinity,
                          ease: 'linear',
                        }}
                        style={{ offsetPath: `path('${path}')` }}
                      />
                    </g>
                  );
                })}

                {/* City markers */}
                {visibleCities.map(city => {
                  const isHQ = city.id === hqCityId;
                  const clickable = isCitySelectable(city);
                  const dotColor = isHQ
                    ? '#10B981'
                    : (clickable ? '#F59E0B' : (city.country === 'FR' ? '#60A5FA' : '#A855F7'));
                  return (
                    <Marker
                      key={city.id}
                      coordinates={[city.lng, city.lat]}
                      data-testid={`city-${city.id}`}
                      onClick={clickable && onSelectCity ? () => onSelectCity(city.id) : undefined}
                      style={clickable ? {
                        default: { cursor: 'pointer', outline: 'none' },
                        hover: { cursor: 'pointer', outline: 'none' },
                        pressed: { cursor: 'pointer', outline: 'none' },
                      } : undefined}
                    >
                      {clickable && (
                        <circle
                          r={9}
                          fill="none"
                          stroke={dotColor}
                          strokeWidth={1.2}
                          opacity={0.65}
                          style={{ animation: 'hst-map-pulse 2.2s ease-out infinite' }}
                        />
                      )}
                      <circle
                        r={isHQ ? 6 : (clickable ? 4.5 : 3)}
                        fill={dotColor}
                        stroke={clickable ? '#FBBF24' : '#0B1220'}
                        strokeWidth={clickable ? 1.5 : 1}
                      />
                      <text
                        x={isHQ ? 9 : (clickable ? 7 : 5)}
                        y={3}
                        style={{
                          fontFamily: 'inherit',
                          fontSize: isHQ ? 9 : (clickable ? 8 : 7),
                          fill: isHQ ? '#10B981' : (clickable ? '#FBBF24' : '#94A3B8'),
                          fontWeight: isHQ || clickable ? 700 : 400,
                          pointerEvents: 'none',
                        }}
                      >
                        {isHQ ? `★ ${city.name}` : city.name}
                      </text>
                    </Marker>
                  );
                })}
              </>
            )}
          </Geographies>
        </ZoomableGroup>
      </ComposableMap>

      {/* Bottom bar : active route list summary */}
      {showRoutesPanel && !selectable && routes.length > 0 && (
        <div className="absolute bottom-0 left-0 right-0 z-10 bg-ink-800/90 backdrop-blur border-t border-ink-600 px-3 py-2 max-h-32 overflow-y-auto" data-testid="map-routes-list">
          <p className="text-[9px] uppercase tracking-wider text-slate-500 font-mono mb-1.5 flex items-center gap-1">
            <Truck className="w-3 h-3" /> {t('map_active_missions_label')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
            {routes.map((r) => (
              <div key={r.contract.instanceId} className="flex items-center gap-2 text-[10px]">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: r.color }} />
                <div className="flex-1 min-w-0">
                  <p className="text-slate-200 truncate">
                    {hq?.name} <span className="text-slate-500">→</span> {r.dest.name}
                  </p>
                  <p className="text-slate-500 font-mono text-[9px]">
                    {r.contract.category} · {r.distance} km
                    {r.isLocalBonus && <span className="text-money ml-1">· {t('map_hq_zone_fuel_bonus')}</span>}
                  </p>
                </div>
                <MapPin className="w-3 h-3 text-slate-500 flex-shrink-0" />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
